function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { usePrevious } from '@deephaven/react-hooks';
import "./Stack.css";
import { SlideTransition } from "../transitions/index.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Returns the React `key` for an element, or `null` for non-elements.
 *
 * The parent that renders the stack may re-render frequently (e.g. an
 * `IrisGrid` re-renders on every table tick) and hand us brand-new element
 * objects that represent the same logical view. Tracking the animating views
 * by `key` (rather than by element reference) means those re-renders neither
 * retrigger the slide animation nor churn state every frame.
 *
 * Children are always run through `React.Children.toArray`, which assigns a
 * stable key to every element, so element views always have a key.
 */
function getViewKey(node) {
  return /*#__PURE__*/React.isValidElement(node) ? node.key : null;
}

/**
 * Finds the child that currently represents the view identified by `key`.
 *
 * Returning the *current* element (instead of a stored copy) keeps the rendered
 * view's content live: when a parent re-renders with a new element instance for
 * the same logical view, the view's latest props are forwarded to the mounted
 * component. This matters for prop-driven panels such as the "Organize Columns"
 * (`VisibilityOrderingBuilder`) sidebar, whose list contents and Undo/Redo state
 * arrive entirely via props from the grid - freezing those props would stop the
 * panel from reflecting moves, hides, groupings, and undo/redo.
 */
function findViewByKey(views, key) {
  var _views$find;
  if (key == null) {
    return null;
  }
  return (_views$find = views.find(view => getViewKey(view) === key)) !== null && _views$find !== void 0 ? _views$find : null;
}

/**
 * Pass a full navigation stack of children, and then automatically does a sliding animation when the stack changes.
 * Adding items to the stack will do a "push" animation, and removing items from the stack will do a "pop" animation.
 */
export function Stack(_ref) {
  var children = _ref.children,
    dataTestId = _ref['data-testid'];
  var childrenArray = useMemo(() => React.Children.toArray(children), [children]);
  var prevChildrenArray = usePrevious(childrenArray);

  // The animating views are tracked by `key`, not element identity, so that
  // frequent parent re-renders (which produce new-but-equivalent elements)
  // neither retrigger animations nor freeze content. The element actually
  // rendered is always looked up from the latest `childrenArray` via
  // `findViewByKey`, keeping each view's props live.
  var _useState = useState(() => getViewKey(childrenArray[childrenArray.length - 1])),
    _useState2 = _slicedToArray(_useState, 2),
    mainViewKey = _useState2[0],
    setMainViewKey = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    pushingViewKey = _useState4[0],
    setPushingViewKey = _useState4[1];

  // The popping view has already been removed from `children`, so we keep the
  // element instance itself - it is only animating out and its content does not
  // need to stay live.
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    poppingView = _useState6[0],
    setPoppingView = _useState6[1];

  /**
   * To do the animation effect, we just need to set the proper pushing/popping views when the stack changes.
   * `mainView` - The main view of the stack, stationary, filling the container.
   * `pushingView` - A view that's currently being pushed. Slides from off screen right to overtop of `mainView`.
   * `poppingView` - A view that's currently being popped. Slides from overtop of `mainView` to right off screen.
   *
   * When the `pushingView` or `poppingView` is set, that will kick off their animation.
   * Completion of the animation is handled in `pushComplete` or `popComplete`, and then the stack is in an idle state again.
   *
   * Views are compared by `key` (see `getViewKey`) so that a parent that
   * re-renders with new element instances for the same logical view does not
   * retrigger animations or churn state on every render.
   */
  useEffect(function initAnimation() {
    if (prevChildrenArray === undefined || childrenArray === prevChildrenArray) {
      return;
    }
    var topChildKey = getViewKey(childrenArray[childrenArray.length - 1]);
    if (pushingViewKey !== null || poppingView !== null) {
      // We're mid-animation. Keep the animating view pointed at the current
      // top view, but don't start a new animation.
      if (pushingViewKey !== null) {
        if (topChildKey !== pushingViewKey) {
          // A different view was pushed mid-animation - animate to it instead
          setPushingViewKey(topChildKey);
        }
      } else if (topChildKey !== mainViewKey) {
        setMainViewKey(topChildKey);
      }
    } else if (childrenArray.length === prevChildrenArray.length || prevChildrenArray.length === 0) {
      // Stack is the same size or we've just mounted - just update the view
      if (topChildKey !== mainViewKey) {
        setMainViewKey(topChildKey);
      }
    } else if (childrenArray.length > prevChildrenArray.length) {
      // Stack has grown - start the push animation
      setPushingViewKey(topChildKey);
    } else {
      // Stack has shrunk - start the pop animation
      setMainViewKey(topChildKey);
      setPoppingView(prevChildrenArray[prevChildrenArray.length - 1]);
    }
  }, [childrenArray, prevChildrenArray, pushingViewKey, poppingView, mainViewKey]);
  var pushComplete = useCallback(() => {
    setMainViewKey(pushingViewKey);
    setPushingViewKey(null);
  }, [pushingViewKey]);
  var popComplete = useCallback(() => {
    setPoppingView(null);
  }, []);
  var mainView = findViewByKey(childrenArray, mainViewKey);
  var pushingView = findViewByKey(childrenArray, pushingViewKey);
  return /*#__PURE__*/_jsxs("div", {
    className: "navigation-stack",
    children: [/*#__PURE__*/_jsx("div", {
      className: "main-view",
      "data-testid": dataTestId,
      children: mainView
    }), /*#__PURE__*/_jsx(SlideTransition, {
      direction: "right",
      in: poppingView != null,
      onEntered: popComplete,
      children: /*#__PURE__*/_jsx(_Fragment, {
        children: poppingView != null && /*#__PURE__*/_jsx("div", {
          className: "popping-view",
          children: poppingView
        })
      })
    }), /*#__PURE__*/_jsx(SlideTransition, {
      in: pushingView != null,
      onEntered: pushComplete,
      children: /*#__PURE__*/_jsx(_Fragment, {
        children: pushingView != null && /*#__PURE__*/_jsx("div", {
          className: "pushing-view",
          children: pushingView
        })
      })
    })]
  });
}
export default Stack;
//# sourceMappingURL=Stack.js.map